import {
  accessOf,
  activityRows,
  attention,
  attentionRows,
  ATTENTION_GROUPS,
  inRange,
  lei,
  localDay,
  offerQuestions,
  performance,
  rangeOf,
  rfqPhase,
  savingsOf,
  supplierInsights,
  todayView,
  recurringNeeds,
  weekAhead,
  type ActivityRow,
  type AttentionRow,
  type GroupKey,
  type RowTarget,
  type Company,
  type RangeKind,
  type Rfq,
  type WeekItem,
  orderedByCategory,
  orderedByMonth,
  t,
} from '@procumi/shared';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AskDialog } from '../components/AskDialog';
import { ActiveRfqsTable, rowStatus, type RfqRow } from '../components/dashboard/ActiveRfqsTable';
import {
  AttentionPanel,
  type AttentionGroup,
  type AttentionItem,
} from '../components/dashboard/AttentionPanel';
import { CATEGORY_SLOTS, CategoryDonut, SpendTrend } from '../components/dashboard/SpendCharts';
import { DashboardHeader, RANGE_TEXT } from '../components/dashboard/DashboardHeader';
import { Bridge, type BridgeStage } from '../components/dashboard/Bridge';
import { Horizon, type HorizonItem } from '../components/dashboard/Horizon';
import { RecentActivity, SupplierPerformance } from '../components/dashboard/Insights';
import { PriceMoves } from '../components/dashboard/PriceMoves';
import { SavingsPanel } from '../components/dashboard/ProcurementChart';
import { money, Num } from '../components/dashboard/shared';
import { FirstSteps } from '../components/FirstSteps';
import { FollowUpDialog } from '../components/FollowUpDialog';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { LIVE_EVENT } from '../lib/live';
import type { Route } from '../routes';

/**
 * „Panou de control” al achizitorului (refăcut 2026-10-13): privește înainte.
 * 1. puntea: unde stau acum achizițiile, de la echipă la furnizori și la comandă;
 * 2. ce cere acțiune și ce vine în 30 de zile (inclusiv comenzile care se repetă);
 * 3. rezultatele: cereri în lucru, bani pe luni și categorii, economii, prețuri.
 *
 * Toate cifrele vin din date reale (`/api/dashboard`, `/api/today`), socotite
 * în browser cu `panel.ts` / `cockpit.ts` (acoperite de teste), dintr-un singur
 * set: perioada și șantierul aleși schimbă tot. Ordinea secțiunilor stă în
 * `LAYOUT`, ca mai târziu un director sau un șef de șantier să aibă alta.
 */

type Block =
  | 'attention'
  | 'horizon'
  | 'table'
  | 'spend'
  | 'categories'
  | 'savings'
  | 'prices'
  | 'suppliers'
  | 'activity';
/** Rândurile panoului; azi o singură perspectivă, a achizitorului. */
const LAYOUT: Block[][] = [
  ['attention', 'horizon'],
  ['table'],
  ['spend', 'categories'],
  ['savings', 'prices'],
  ['suppliers', 'activity'],
];

export function DashboardPage({
  company,
  onCompanyChange,
  go,
  toast,
  signature,
  userName,
}: {
  company: Company;
  onCompanyChange: (c: Company) => void;
  go: (r: Route) => void;
  toast: Toast;
  signature: string;
  userName: string;
}) {
  const { data, error, loading, reload } = useAsync(() => api.today(), [], 'today');
  const sumar = useAsync(() => api.dashboard(), [], 'dashboard');
  const preturi = useAsync(() => api.prices('12'), [], 'prices:12');
  // Firma nouă, care n-a făcut încă nimic: panoul arată doar „Primii pași” (aceleași condiții ca lista).
  const acc = accessOf(company);
  const firstStepsShown = !company.onboardingHidden && acc.state !== 'legacy' && acc.writable;
  const facts = useAsync(
    () => (firstStepsShown ? api.onboarding() : Promise.resolve(null)),
    [firstStepsShown],
    firstStepsShown ? 'onboarding' : undefined,
  );
  const fresh = firstStepsShown && facts.data?.started === false;
  const [followUp, setFollowUp] = useState<{ rfq: Rfq; sendId: string } | null>(null);
  const [asking, setAsking] = useState<Rfq | null>(null);
  const [busy, setBusy] = useState('');
  const [santier, setSantier] = useState('');
  const [range, setRange] = useState<RangeKind>('month');
  const today0 = localDay(Date.now());
  const [custom, setCustom] = useState({ from: `${today0.slice(0, 8)}01`, to: today0 });

  // notificările (oferte, comenzi, livrări) refac panoul, adunat
  const reloads = useRef({ reload, sumar: sumar.reload });
  reloads.current = { reload, sumar: sumar.reload };
  useEffect(() => {
    let t: number | undefined;
    const on = () => {
      window.clearTimeout(t);
      t = window.setTimeout(() => {
        reloads.current.reload();
        reloads.current.sumar();
      }, 800);
    };
    window.addEventListener(LIVE_EVENT, on);
    return () => {
      window.removeEventListener(LIVE_EVENT, on);
      window.clearTimeout(t);
    };
  }, []);

  const acum = useMemo(() => Date.now(), [data, sumar.data]);
  const today = localDay(acum);
  const d = sumar.data;
  const r = useMemo(() => rangeOf(range, today, custom), [range, today, custom]);

  const peSantier = <T extends { site: { id: string } }>(rows: T[]) =>
    santier ? rows.filter((x) => x.site.id === santier) : rows;
  const todayRfqs = useMemo(() => peSantier(data ?? []), [data, santier]);
  const view = useMemo(() => todayView(todayRfqs, acum), [todayRfqs, acum]);
  const orders = useMemo(() => peSantier(d?.orders ?? []), [d, santier]);
  const rfqs = useMemo(() => peSantier(d?.rfqs ?? []), [d, santier]);
  const stats = useMemo(() => peSantier(d?.stats ?? []), [d, santier]);
  const toOrder = useMemo(
    () => (d?.toOrder ?? []).filter((x) => !santier || x.rfq.site.id === santier),
    [d, santier],
  );
  const alerte = useMemo(() => attention(rfqs, orders, view, today), [rfqs, orders, view, today]);

  // ---------------------------------------------------------------- cifrele perioadei
  const m = useMemo(() => {
    const inR = (t: string | null | undefined) => inRange(t, r.from, r.to);
    const inP = (t: string | null | undefined) => inRange(t, r.prevFrom, r.prevTo);
    const sum = (f: (t: string) => boolean) =>
      orders.filter((o) => f(o.issuedAt)).reduce((n, o) => n + o.valueLei, 0);
    const statsIn = stats.filter((s) => inR(s.createdAt));
    const statsPrev = stats.filter((s) => inP(s.createdAt));
    return {
      purchased: sum(inR),
      purchasedPrev: sum(inP),
      purchasedCount: orders.filter((o) => inR(o.issuedAt)).length,
      savings: savingsOf(statsIn),
      savingsPrev: savingsOf(statsPrev),
      created: statsIn.length,
      createdPrev: statsPrev.length,
      perf: performance(stats, r.to, 7),
      suppliers: supplierInsights(stats, r.from, r.to),
      statsIn,
      byMonth: orderedByMonth(orders, r.to, 12),
      byCategory: orderedByCategory(orders, r.from, r.to, CATEGORY_SLOTS),
    };
  }, [orders, stats, r]);

  const santiere = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const x of data ?? []) map.set(x.site.id, x.site);
    for (const x of d?.rfqs ?? []) map.set(x.site.id, x.site);
    for (const x of d?.orders ?? []) map.set(x.site.id, x.site);
    for (const x of d?.stats ?? []) if (!map.has(x.site.id)) map.set(x.site.id, x.site);
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'ro'));
  }, [data, d]);

  const siteOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of d?.stats ?? []) map.set(s.id, s.site.id);
    for (const x of d?.rfqs ?? []) map.set(x.id, x.site.id);
    return map;
  }, [d]);
  const activity = useMemo(
    () =>
      activityRows(
        (d?.events ?? []).filter((e) => !santier || (e.rfqId && siteOf.get(e.rfqId) === santier)),
        8,
      ),
    [d, santier, siteOf],
  );
  const week = useMemo(
    () => weekAhead(orders, rfqs, santier ? [] : (d?.requisitions ?? []), today, 30),
    [orders, rfqs, d, santier, today],
  );

  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox error={error ?? t('Nu am putut încărca.')} onRetry={reload} />;

  // ---------------------------------------------------------------- acțiunile
  const openFollowUp = async (rfqId: string, sendId: string) => {
    setBusy(sendId);
    try {
      setFollowUp({ rfq: await api.rfq(rfqId), sendId });
    } catch (e) {
      toast(errMsg(e));
      reload();
    } finally {
      setBusy('');
    }
  };
  const openAsk = async (rfqId: string, sendId: string) => {
    setBusy(sendId);
    try {
      const rfq = await api.rfq(rfqId);
      if (offerQuestions(rfq)) setAsking(rfq);
      else {
        toast(t('Nu mai e nimic de întrebat la cererea asta'));
        reload();
      }
    } catch (e) {
      toast(errMsg(e));
      reload();
    } finally {
      setBusy('');
    }
  };
  const refresh = () => {
    reload();
    sumar.reload();
  };
  const openRfq = (id: string) => go({ view: 'rfq', id });
  const openOrder = (rfqId: string, orderId: string) => go({ view: 'order', rfqId, orderId });
  const follow = (rfqId: string, sendId: string) => (
    <button className="btn small" disabled={busy === sendId} onClick={() => void openFollowUp(rfqId, sendId)}>
      {busy === sendId ? '…' : 'Follow-up'}
    </button>
  );
  // Rândurile și grupurile vin din `shared` (aceleași ca în mailul de dimineață);
  // aici se adaugă doar ce face fiecare buton.
  const dashRfq = new Map((d?.rfqs ?? []).map((x) => [x.id, x]));
  const toApprove = santier ? [] : (d?.toApprove ?? []);
  const requisitions = santier ? [] : (d?.requisitions ?? []);
  const rows = attentionRows({
    alerts: alerte,
    decide: view.decide.map((row) => ({
      ...row,
      best: dashRfq.get(row.rfq.id)?.best?.total ?? null,
    })),
    toOrder,
    requisitions,
  });
  const rfqOf = (r: AttentionRow): string => (r.target.kind === 'requisition' ? '' : r.target.rfqId);
  const openTarget = (t: RowTarget) =>
    t.kind === 'requisition'
      ? go({ view: 'requisition', id: t.id })
      : t.kind === 'order'
        ? openOrder(t.rfqId, t.orderId)
        : openRfq(t.rfqId);
  const actionFor = (r: AttentionRow): ReactNode => {
    switch (r.kind) {
      case 'silent':
        return r.sendId ? follow(rfqOf(r), r.sendId) : undefined;
      case 'ask':
        return r.sendId ? (
          <button
            className="btn small"
            disabled={busy === r.sendId}
            onClick={() => void openAsk(rfqOf(r), r.sendId!)}
          >
            {busy === r.sendId ? '…' : t('Întreabă')}
          </button>
        ) : undefined;
      case 'decide':
        return (
          <button className="btn small primary" onClick={() => openRfq(rfqOf(r))}>
            {t('Compară')}
          </button>
        );
      case 'order':
        return (
          <button className="btn small" onClick={() => go({ view: 'rfq', id: rfqOf(r), openOrder: true })}>
            {t('Fă comanda')}
          </button>
        );
      case 'req':
        return (
          <button className="btn small" onClick={() => openTarget(r.target)}>
            {t('Preia')}
          </button>
        );
      default:
        return undefined;
    }
  };
  const item = (r: AttentionRow): AttentionItem => ({
    key: r.key,
    title: r.title,
    detail: r.detail,
    open: () => openTarget(r.target),
    action: actionFor(r),
  });
  const approveItems: AttentionItem[] = toApprove.map((a) => ({
    key: `a-${a.orderId}`,
    title: `${a.number} · ${a.supplier}`,
    detail: `${a.rfqTitle} · ${a.siteName} · ${lei(a.valueLei)}`,
    open: () => openOrder(a.rfqId, a.orderId),
    action: (
      <button className="btn small" onClick={() => openOrder(a.rfqId, a.orderId)}>
        {t('Aprobă')}
      </button>
    ),
  }));
  const inGroup = (k: GroupKey) => rows.filter((r) => r.group === k);
  const first = (k: GroupKey) => () => {
    const r = inGroup(k)[0];
    if (r) openTarget(r.target);
  };
  const firstSilent = rows.find((r) => r.kind === 'silent' && r.sendId);
  const firstAsk = rows.find((r) => r.kind === 'ask' && r.sendId);
  const runs: Record<GroupKey, () => void> = {
    overdue: () =>
      firstSilent ? void openFollowUp(rfqOf(firstSilent), firstSilent.sendId!) : first('overdue')(),
    late: first('late'),
    decide: first('decide'),
    approve: () => toApprove[0] && openOrder(toApprove[0].rfqId, toApprove[0].orderId),
    order: () => {
      const r = inGroup('order')[0];
      if (r) go({ view: 'rfq', id: rfqOf(r), openOrder: true });
    },
    offers: () => (firstAsk ? void openAsk(rfqOf(firstAsk), firstAsk.sendId!) : first('offers')()),
    site: first('site'),
    req: first('req'),
  };
  const groups: AttentionGroup[] = ATTENTION_GROUPS.map((g) => ({
    ...g,
    cta: g.key === 'offers' && firstAsk ? t('Întreabă') : g.cta,
    run: runs[g.key],
    items: g.key === 'approve' ? approveItems : inGroup(g.key).map(item),
  }));

  // ---------------------------------------------------------------- cererile în lucru
  const silentOf = new Map<string, string>();
  for (const a of alerte)
    if (a.kind === 'silent' && a.sendId && !silentOf.has(a.rfqId)) silentOf.set(a.rfqId, a.sendId);
  const active = rfqs.filter((x) => !x.closed);
  const rfqRows: RfqRow[] = active.map((x) => {
    const status = rowStatus(rfqPhase(x, view), x);
    const sid = silentOf.get(x.id);
    return {
      r: x,
      status,
      action:
        status === 'ready' ? (
          <button className="btn small primary" onClick={() => openRfq(x.id)}>
            {t('Compară')}
          </button>
        ) : status === 'order' ? (
          <button className="btn small" onClick={() => go({ view: 'rfq', id: x.id, openOrder: true })}>
            {t('Fă comanda')}
          </button>
        ) : sid ? (
          follow(x.id, sid)
        ) : status === 'received' ? (
          <button className="btn small ghost" onClick={() => openRfq(x.id)}>
            {t('Vezi ofertele')}
          </button>
        ) : (
          <button className="btn small ghost" onClick={() => openRfq(x.id)}>
            {t('Deschide')}
          </button>
        ),
    };
  });
  const waitingRows = rfqRows.filter(
    (x) => x.status === 'waiting' || x.status === 'received' || x.status === 'late',
  );
  const ready = view.decide.length;

  // ---------------------------------------------------------------- puntea și orizontul
  const siteName = santiere.find((x) => x.id === santier)?.name;
  // șantierele active ale firmei, nu doar cele cu cereri: o firmă nouă are deja unul
  const siteCount = d?.sites ?? santiere.length;
  const label = RANGE_TEXT[range];
  const overdueN = groups[0]!.items.length;
  const s = m.savings;
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const decideNote = [
    ready ? plural(ready, t('comparație gata'), t('comparații gata')) : null,
    toOrder.length ? plural(toOrder.length, t('comandă de trimis'), t('comenzi de trimis')) : null,
  ].filter(Boolean);
  const stages: BridgeStage[] = [
    {
      key: 'team',
      icon: 'clipboard',
      tone: 'slate',
      label: t('Cereri din șantiere'),
      value: requisitions.length,
      note: requisitions[0]
        ? `${requisitions[0].author}: ${requisitions[0].title}`
        : t('Nicio cerere nouă din șantiere'),
      cta: requisitions[0]
        ? { label: t('Preia'), run: () => go({ view: 'requisition', id: requisitions[0]!.id }) }
        : undefined,
      open: () => go({ view: 'requisitions' }),
    },
    {
      key: 'out',
      icon: 'send',
      tone: overdueN ? 'red' : 'blue',
      label: t('La furnizori'),
      value: waitingRows.length,
      note: overdueN
        ? plural(overdueN, t('răspuns întârziat'), t('răspunsuri întârziate'))
        : waitingRows.length
          ? t('Toți furnizorii sunt în termen')
          : t('Nicio cerere la furnizori acum'),
      alert: overdueN > 0,
      cta: overdueN ? { label: 'Follow-up', run: () => void groups[0]!.run() } : undefined,
      open: () => go({ view: 'rfqs', state: 'open' }),
    },
    {
      key: 'decide',
      icon: 'compare',
      tone: 'blue',
      label: t('De decis'),
      value: ready + toOrder.length,
      note: decideNote.length ? decideNote.join(', ') : t('Nimic de hotărât acum'),
      cta: ready
        ? { label: t('Compară'), run: () => void groups[2]!.run() }
        : toOrder.length
          ? { label: t('Fă comanda'), run: () => void groups[4]!.run() }
          : undefined,
    },
    {
      key: 'done',
      icon: 'box',
      tone: 'night',
      label: t('Comandat, {0}', label.toLowerCase()),
      value: <Num value={m.purchased} format={money} />,
      note: s.realized ? (
        <>
          {plural(m.purchasedCount, t('comandă'), t('comenzi'))}, <b>{money(s.realized)}</b>{' '}
          {t('economisiți')}
        </>
      ) : (
        plural(m.purchasedCount, t('comandă emisă'), t('comenzi emise'))
      ),
      open: () => go({ view: 'orders' }),
    },
  ];

  const openWeek = (it: WeekItem) =>
    it.orderId
      ? openOrder(it.rfqId!, it.orderId)
      : it.requisitionId
        ? go({ view: 'requisition', id: it.requisitionId })
        : it.rfqId
          ? openRfq(it.rfqId)
          : undefined;
  const toH = (it: WeekItem): HorizonItem => ({
    key: it.key,
    kind: it.kind,
    day: it.day,
    title: it.title,
    detail:
      it.kind === 'delivery'
        ? `${it.detail}${it.lei ? `, ${money(it.lei)}` : ''}`
        : it.kind === 'deadline'
          ? t('Au răspuns {0}', it.detail)
          : it.detail,
    open: () => openWeek(it),
  });
  const forecast: HorizonItem[] = recurringNeeds(orders, rfqs, today, 30).map((n) => ({
    key: `f-${n.category}`,
    kind: 'forecast',
    day: n.nextDay < today ? today : n.nextDay,
    title: n.category,
    detail: t(
      'Comandat de {0} ori, cam la {1} zile; de obicei {2}, ultima dată la {3}',
      n.times,
      n.everyDays,
      money(n.typicalLei),
      n.lastSupplier,
    ),
    open: () => openRfq(n.lastRfqId),
    action: (
      <button className="btn small" onClick={() => go({ view: 'new' })}>
        {t('Cere oferte')}
      </button>
    ),
  }));
  const horizon = [...week.days.flatMap((x) => x.items).map(toH), ...forecast].sort((a, b) =>
    a.day.localeCompare(b.day),
  );

  const blocks: Record<Block, ReactNode> = {
    attention: <AttentionPanel key={santier} groups={groups} />,
    horizon: <Horizon items={horizon} overdue={week.overdue.map(toH)} today={today} />,
    table: (
      <ActiveRfqsTable
        rows={rfqRows}
        today={today}
        sites={santiere}
        site={santier}
        onSite={setSantier}
        onOpen={openRfq}
        onAll={() => go({ view: 'rfqs', state: 'open' })}
      />
    ),
    spend: <SpendTrend months={m.byMonth} from={r.from} to={r.to} onAll={() => go({ view: 'orders' })} />,
    categories: <CategoryDonut rows={m.byCategory} rangeLabel={label} />,
    savings: (
      <SavingsPanel
        months={m.perf}
        savings={s}
        rangeLabel={label}
        onOpenRfq={openRfq}
        onAll={() => go({ view: 'analysis' })}
      />
    ),
    prices: (
      <PriceMoves
        products={preturi.data?.products ?? []}
        onOpen={(key) => go({ view: 'prices', material: key })}
        onAll={() => go({ view: 'prices' })}
      />
    ),
    suppliers: <SupplierPerformance rows={m.suppliers} onAll={() => go({ view: 'analysis' })} />,
    activity: (
      <RecentActivity
        rows={activity}
        now={acum}
        onAll={() => go({ view: 'analysis' })}
        onOpen={(a: ActivityRow) =>
          a.orderId ? openOrder(a.rfqId!, a.orderId) : a.rfqId ? openRfq(a.rfqId) : undefined
        }
      />
    ),
  };

  return (
    <div className="db">
      <DashboardHeader
        name={userName}
        facts={t(
          'Achizițiile pe {0}',
          siteName ?? `${siteCount} ${siteCount === 1 ? t('șantier') : t('șantiere')}`,
        )}
        sites={santiere}
        site={santier}
        onSite={setSantier}
        range={range}
        custom={custom}
        onRange={(k, c) => {
          setRange(k);
          if (c) setCustom(c);
        }}
        onNew={() => go({ view: 'new' })}
        onHelp={() => go({ view: 'help' })}
      />

      <FirstSteps company={company} go={go} toast={toast} onHidden={onCompanyChange} />

      {fresh ? (
        <p className="hint db-fresh">
          {t('Cifrele, termenele și economiile apar aici după prima cerere de ofertă.')}
        </p>
      ) : sumar.loading ? (
        <Loading />
      ) : sumar.error ? (
        <ErrorBox error={sumar.error} onRetry={sumar.reload} />
      ) : (
        <>
          <Bridge stages={stages} />
          {LAYOUT.map((row) => (
            <div key={row.join('-')} className={`db-row db-row-${row.length}`}>
              {row.map((b) => (
                <div key={b} className={`db-slot db-slot-${b}`}>
                  {blocks[b]}
                </div>
              ))}
            </div>
          ))}
        </>
      )}

      {asking && offerQuestions(asking) ? (
        <AskDialog
          rfq={asking}
          send={asking.sends.find((x) => x.id === offerQuestions(asking)!.sendId)!}
          questions={offerQuestions(asking)!.questions}
          signature={signature}
          toast={toast}
          onAsked={refresh}
          onClose={() => setAsking(null)}
        />
      ) : null}
      {followUp && followUp.rfq.sends.some((x) => x.id === followUp.sendId) ? (
        <FollowUpDialog
          rfq={followUp.rfq}
          send={followUp.rfq.sends.find((x) => x.id === followUp.sendId)!}
          signature={signature}
          toast={toast}
          onSent={refresh}
          onClose={() => setFollowUp(null)}
        />
      ) : null}
    </div>
  );
}
